import { browser } from '$app/env';
import { PUBLIC_API_BASE_URL } from '$app/env/public';
import { localStorageSupported } from '#lib/helper/browser.js';
import { businessStore, createDefaultBusiness } from '#lib/stores/business.js';
import { loadUser, userStore } from '#lib/stores/user.js';
import type { ApiError } from '#lib/types/generated/index.js';
import axios, { type AxiosInstance } from 'axios';
import { derived, get, writable } from 'svelte/store';
import { addDiagnostic, sanitizeDetails } from './diagnostics';
import { t } from './i18n';
import { toastError } from './toast';

export const AUTH_LAST_REFRESH_STORAGE_KEY = 'auth_last_refresh_at';

declare module 'axios' {
	export interface AxiosRequestConfig {
		skipToastError?: boolean;
	}
	export interface InternalAxiosRequestConfig {
		_apiLogId?: string;
		_startTime?: number;
		skipToastError?: boolean;
		_retry?: boolean;
		_authRetries?: number;
	}
}

export function handleApiError<T = void>(
	e: unknown,
	handler: (err: ApiError & Record<string, unknown>) => T
): T {
	if (axios.isAxiosError(e)) {
		const data = e.response?.data as Partial<ApiError> | undefined;
		return handler({
			error: data?.error || e.message || 'error.unknown',
			httpCode: data?.httpCode ?? e.response?.status ?? 0,
			field: data?.field ?? null,
			...data
		});
	}

	if (typeof e === 'object' && e !== null && 'error' in e && 'httpCode' in e) {
		return handler(e as ApiError & Record<string, unknown>);
	}

	// Fail-Fast: relance les bugs JavaScript locaux (TypeError, etc.) vers Sentry
	throw e;
}

const apiStore = writable<AxiosInstance | undefined>(undefined);
export const isApiReady = derived(apiStore, ($api) => !!$api);

export function getApi(): AxiosInstance {
	let api = get(apiStore);
	if (!api) {
		if (browser) {
			initializeApi();
		} else {
			initBackendApi();
		}
		api = get(apiStore);
	}
	if (!api) {
		throw new Error(
			'API client is not initialized. Ensure initializeApi() or initBackendApi() has been called.'
		);
	}
	return api;
}

let isRefreshing = false;
let isOffline = false;
let listeningOnline = false;
let lastRefreshTime = 0;
type RequestQueue = {
	resolve: (value?: unknown) => void;
	reject: (reason?: unknown) => void;
};
let failedQueue: Array<RequestQueue> = [];
let apiInitialized = false;
let securedPage = true;

function enqueueRequest(): Promise<unknown> {
	return new Promise((resolve, reject) => {
		failedQueue.push({ resolve, reject });
	});
}

function flushQueue(): void {
	if (isOffline || isRefreshing) return;
	const queue = failedQueue;
	failedQueue = [];
	queue.forEach((req) => req.resolve());
}

function rejectQueue(err: unknown): void {
	const queue = failedQueue;
	failedQueue = [];
	queue.forEach((req) => req.reject(err));
}

async function runRefresh(api: AxiosInstance): Promise<void> {
	const refreshStartedAt = Date.now();
	try {
		await api.post('/auth/refresh', undefined, { skipToastError: true });
		lastRefreshTime = Date.now();
		if (browser && localStorageSupported()) {
			localStorage.setItem(AUTH_LAST_REFRESH_STORAGE_KEY, String(lastRefreshTime));
		}
		isRefreshing = false;
		flushQueue();
	} catch (refreshErr) {
		if (browser && (!axios.isAxiosError(refreshErr) || !refreshErr.response)) {
			markOffline(api);
			return;
		}

		if (
			browser &&
			localStorageSupported() &&
			Number(localStorage.getItem(AUTH_LAST_REFRESH_STORAGE_KEY)) >= refreshStartedAt
		) {
			isRefreshing = false;
			flushQueue();
			return;
		}

		if (securedPage && window.location.pathname !== '/') {
			console.log('Refresh token failed, redirecting and freezing context');
			window.location.href = '/';
			failedQueue = [];
			return;
		}

		isRefreshing = false;
		rejectQueue(refreshErr);
	}
}

function markOffline(api: AxiosInstance): void {
	isOffline = true;
	if (!browser || listeningOnline) return;
	listeningOnline = true;
	window.addEventListener(
		'online',
		() => {
			listeningOnline = false;
			isOffline = false;
			if (isRefreshing) {
				void runRefresh(api);
			} else {
				flushQueue();
			}
		},
		{ once: true }
	);
}

export function getApiBaseUrl(): string {
	return PUBLIC_API_BASE_URL;
}

export function initBackendApi(cookieToken: string | null = null) {
	let baseURL = PUBLIC_API_BASE_URL;
	if (baseURL == 'https://api2.reservatoo.dev') {
		baseURL = 'http://api';
	}
	const api = axios.create({ baseURL });

	api.interceptors.request.use(
		(config) => {
			if (cookieToken) {
				// Auth with the cookie token
				config.headers.Cookie = `token=${cookieToken}`;
			} else {
				// Auth with the app authorized tokens
				config.headers.Authorization = `Bearer ${process.env.AUTHORIZED_TOKENS}`;
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	apiInitialized = true;
	apiStore.set(api);
}

export function initPublicApi() {
	const baseURL = PUBLIC_API_BASE_URL;
	const api = axios.create({ baseURL });
	api.defaults.withCredentials = true;

	api.interceptors.request.use(
		(config) => {
			if (browser && typeof window !== 'undefined' && window.location.href) {
				config.headers['x-app-caller-url'] = window.location.href;
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	apiInitialized = true;
	apiStore.set(api);
}

export const loadingStore = writable<number>(0);

export interface ApiLogEntry {
	id: string;
	method: string;
	url: string;
	payload?: unknown;
	timestamp: string;
	status?: number;
	durationMs?: number;
	response?: unknown;
	errorResponse?: unknown;
	isError?: boolean;
}

export const apiLogStore = writable<ApiLogEntry[]>([]);

let apiLogCounter = 0;

function updateApiLogResponse(
	id?: string,
	status?: number,
	startTime?: number,
	response?: unknown,
	isError?: boolean
): void {
	if (!id) return;
	const durationMs = startTime ? Date.now() - startTime : undefined;
	apiLogStore.update((logs) =>
		logs.map((log) =>
			log.id === id
				? {
						...log,
						status,
						durationMs,
						response,
						errorResponse: isError ? response : undefined,
						isError
					}
				: log
		)
	);
}

export function initializeApi(secure: boolean = true) {
	if (apiInitialized) return;

	securedPage = secure;

	let baseURL;
	if (browser) {
		baseURL = PUBLIC_API_BASE_URL;
	} else {
		baseURL = PUBLIC_API_BASE_URL;
		if (baseURL == 'https://api2.reservatoo.dev') {
			baseURL = 'http://api';
		}
	}
	// baseURL = "https://api2.reservatoo.com";
	const api = axios.create({ baseURL });

	// Interceptor to enable loadingStore
	api.interceptors.request.use(
		(config) => {
			loadingStore.update((n) => n + 1);
			return config;
		},
		(error) => Promise.reject(error)
	);

	// Interceptor to disable loadingStore
	api.interceptors.response.use(
		(response) => {
			loadingStore.update((n) => n - 1);
			return response;
		},
		(error) => {
			loadingStore.update((n) => n - 1);
			return Promise.reject(error);
		}
	);

	// Interceptor to add token in header
	api.interceptors.request.use(
		(config) => {
			config.withCredentials = true;
			return config;
		},
		(error) => Promise.reject(error)
	);

	// Interceptor to add current business id and caller URL in header
	api.interceptors.request.use(
		(config) => {
			const currentBusinessId = get(businessStore).id || get(userStore).defaultBusinessId;
			if (currentBusinessId) {
				config.headers['x-app-current-business-id'] = currentBusinessId;
			}
			if (browser && typeof window !== 'undefined' && window.location.href) {
				config.headers['x-app-caller-url'] = window.location.href;
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	// Interceptor to add current business id in header
	api.interceptors.request.use(
		async (config) => {
			if (browser) {
				if (config.url && !config.url.startsWith('/auth')) {
					if (!navigator.onLine) {
						markOffline(api);
					}
					if (isOffline || isRefreshing) {
						await enqueueRequest();
					}
				}

				let payload: unknown;
				if (config.data !== undefined && config.data !== null) {
					if (typeof config.data === 'string') {
						try {
							payload = sanitizeDetails(JSON.parse(config.data));
						} catch {
							payload = config.data;
						}
					} else {
						payload = sanitizeDetails(config.data);
					}
				} else if (config.params !== undefined && config.params !== null) {
					payload = sanitizeDetails(config.params);
				}

				apiLogCounter++;
				const newEntry: ApiLogEntry = {
					id: `api-${Date.now()}-${apiLogCounter}`,
					method: (config.method || 'GET').toUpperCase(),
					url: config.url || '',
					payload,
					timestamp: new Date().toISOString()
				};

				config._apiLogId = newEntry.id;
				config._startTime = Date.now();

				const current = get(apiLogStore);
				if (current.length >= 200) {
					apiLogStore.set([...current.slice(current.length - 199), newEntry]);
				} else {
					apiLogStore.set([...current, newEntry]);
				}
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	// Interceptor for refresh token
	api.interceptors.response.use(
		(response) => {
			if (browser) {
				const cfg = response.config;
				const responseData =
					response.data !== undefined ? sanitizeDetails(response.data) : undefined;
				updateApiLogResponse(
					cfg._apiLogId,
					response.status,
					cfg._startTime,
					responseData,
					false
				);
			}
			return response;
		},
		async (error) => {
			const originalRequest = error.config;
			if (browser) {
				const cfg = error.config;
				const rawError =
					error.response?.data?.error ?? error.message ?? 'Unknown API error';
				const errorPayload = error.response?.data
					? sanitizeDetails(error.response.data)
					: rawError;
				updateApiLogResponse(
					cfg?._apiLogId,
					error.response?.status ?? 0,
					cfg?._startTime,
					errorPayload,
					true
				);
			}

			if (
				browser &&
				!error.response &&
				originalRequest &&
				!originalRequest.url?.startsWith('/auth')
			) {
				markOffline(api);
				return enqueueRequest().then(() => api.request(originalRequest));
			}

			if (
				error.response?.status === 401 &&
				error.response?.data?.error === 'Invalid business' &&
				!originalRequest._retry
			) {
				originalRequest._retry = true;
				businessStore.set(createDefaultBusiness());
				try {
					await loadUser();
					return api.request(originalRequest);
				} catch {
					// Fall through if loadUser fails
				}
			}

			const MAX_AUTH_REFRESH_ATTEMPTS = 2;
			const authRetries = originalRequest._authRetries ?? 0;

			if (
				error.response?.status === 401 &&
				originalRequest.url &&
				!originalRequest.url.startsWith('/auth') &&
				authRetries < MAX_AUTH_REFRESH_ATTEMPTS
			) {
				originalRequest._authRetries = authRetries + 1;
				if (browser && localStorageSupported()) {
					lastRefreshTime = Math.max(
						lastRefreshTime,
						Number(localStorage.getItem(AUTH_LAST_REFRESH_STORAGE_KEY)) || 0
					);
				}

				if (originalRequest._startTime && originalRequest._startTime < lastRefreshTime) {
					return api.request(originalRequest);
				}

				const queuedPromise = enqueueRequest().then(() => api.request(originalRequest));

				if (!isRefreshing) {
					isRefreshing = true;
					void runRefresh(api);
				}

				return queuedPromise;
			}

			if (error.response?.status === 503 && !originalRequest._retry) {
				// Retry once
				originalRequest._retry = true;
				return api.request(originalRequest);
			}

			const status = error.response?.status ?? 0;
			const method = String(error.config?.method ?? 'unknown').toUpperCase();
			const url = String(error.config?.url ?? 'unknown');
			const rawError = error.response?.data?.error ?? error.message ?? 'Unknown API error';

			addDiagnostic({
				category: 'api',
				severity: status >= 500 ? 'error' : status >= 400 ? 'error' : 'warning',
				title: `${method} ${url} [${status}]`,
				message: typeof rawError === 'string' ? rawError : JSON.stringify(rawError),
				url,
				details: {
					status,
					method,
					url,
					payload: error.config?.data,
					response: error.response?.data
				}
			});

			if (error.config?.skipToastError) {
				return Promise.reject(error);
			}

			if (get(businessStore).id || get(userStore).defaultBusinessId) {
				const errorKey = error.response?.data?.error || error.message;
				const translatedError =
					typeof errorKey === 'string' && errorKey.startsWith('error.')
						? get(t)(errorKey)
						: errorKey;

				toastError(
					get(t)('server.error', {
						error: translatedError
					})
				);
				console.error(errorKey);
			}
			return Promise.reject(error);
		}
	);

	apiInitialized = true;
	apiStore.set(api);

	return () => {
		apiInitialized = false;
		isRefreshing = false;
		isOffline = false;
		listeningOnline = false;
		lastRefreshTime = 0;
		if (browser && localStorageSupported()) {
			localStorage.removeItem(AUTH_LAST_REFRESH_STORAGE_KEY);
		}
		failedQueue = [];
		apiStore.set(undefined);
	};
}
